import React, { useState } from "react";
import { Send, Mail, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { sendContact } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function ContactBar() {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await sendContact(form);
      toast.success(t("contactSuccess"));
      setForm({ name: "", email: "", company: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(t("contactError"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-border bg-background px-3 py-2 font-mono text-sm outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary";

  return (
    <section className="border-t border-border" data-testid="contact-bar">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <button
          type="button"
          data-testid="contact-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-start gap-2 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
        >
          <Mail className="h-4 w-4" />
          {t("contactCta")}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="grid grid-cols-1 gap-6 border-t border-border py-6 lg:grid-cols-[1fr,1.4fr] lg:items-center" data-testid="contact-panel">
            <div>
              <h2 className="font-display text-xl font-black tracking-tight">{t("contactTitle")}</h2>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">{t("contactSubtitle")}</p>
              <a
                href="mailto:info@meterox.com"
                data-testid="contact-email-link"
                className="mt-3 inline-flex items-center gap-2 font-mono text-sm text-foreground transition-colors hover:text-primary"
              >
                <Mail className="h-4 w-4 text-primary" /> info@meterox.com
              </a>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input data-testid="contact-name" className={inputCls} placeholder={t("contactName")} value={form.name} onChange={set("name")} required />
                <input data-testid="contact-email" type="email" className={inputCls} placeholder={t("contactEmail")} value={form.email} onChange={set("email")} required />
              </div>
              <input data-testid="contact-company" className={inputCls} placeholder={t("contactCompany")} value={form.company} onChange={set("company")} />
              <textarea data-testid="contact-message" className={`${inputCls} min-h-[80px] resize-y`} placeholder={t("contactMessage")} value={form.message} onChange={set("message")} required />
              <Button
                data-testid="contact-submit"
                type="submit"
                disabled={sending}
                className="w-full rounded-none bg-primary font-semibold text-primary-foreground hover:opacity-90 sm:w-auto"
              >
                {sending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("contactSending")}</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" /> {t("contactSend")}</>
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
